/**
 * Multi-stage stuck detection shared by ChaseSystem and ReturnToPostBehavior.
 *
 * When no progress is detected for `stuckThresholdMs`, triggers the next
 * escalation stage. Each stage gives the caller a progressively more
 * aggressive escape strategy:
 *
 *   Stage 1 — Bump A* budget (try harder to find a path)
 *   Stage 2 — Try reverse pathfinding (T→A reversed, different approach angle)
 *   Stage 3 — Nudge to adjacent tile (physically escape the dead zone)
 *   Stage 4 — Terminal action (force arrival or relocate post)
 *
 * Each stage is cheap/free unless needed. The escalation level persists
 * between timer fires so callers can use it for ongoing decisions
 * (e.g. "use escalated budget while level >= 1").
 *
 * Progress is measured in two ways:
 *   - Net displacement from anchor: when `requireMovementStall` is true, the
 *     timer only accumulates while the entity stays within `STUCK_ANCHOR_RADIUS`
 *     px of the position where it first appeared stuck. Micro-oscillation
 *     (twitching 2-3px between a reversed path waypoint and a physics blocker)
 *     stays within the radius and is correctly detected as stuck, while genuine
 *     movement that covers real distance resets the timer.
 *   - Distance improvement: if the entity gets at least `progressThresholdPx`
 *     closer to the target, the stuck timer and escalation reset.
 *
 * The `persistEscalation` option controls whether the escalation level survives
 * a progress reset. ReturnToPostBehavior keeps escalation alive through progress
 * (once the enemy needed 2000 nodes to start moving, keep using 2000 for the
 * whole trip). ChaseSystem resets escalation together with the timer.
 */

/** Net displacement radius (px) from the anchor position. If the entity stays
 *  within this radius, the stuck timer accumulates. If it escapes, the timer
 *  resets and the anchor moves to the new position. Must exceed micro-oscillation
 *  amplitude (~3px) but be small enough that genuine wall-slides clear it quickly. */
const STUCK_ANCHOR_RADIUS = 8;

/** Result returned by StuckDetector.update() each frame. */
export interface StuckResult {
  /** Escalation stage that just triggered this frame (0 = nothing fired).
   *  Callers map stage numbers to specific escape strategies. */
  triggered: number;
}

/** Options controlling stuck detection behavior. */
export interface StuckDetectorOptions {
  /**
   * Minimum distance improvement (px) toward the target that resets the stuck timer.
   * Matches `DUTY_STUCK_PROGRESS` from `ai/constants.ts` in the standard configuration.
   */
  progressThresholdPx: number;

  /**
   * If true, the escalation level survives a progress reset. Used by ReturnToPostBehavior:
   * once a high-budget path was needed to escape a jam, keep using it for the whole trip.
   * If false (ChaseSystem), escalation resets together with the timer when progress is seen.
   */
  persistEscalation: boolean;

  /**
   * If true, the stuck timer only accumulates when the entity has not moved far from
   * its anchor position (net displacement < STUCK_ANCHOR_RADIUS). Micro-oscillation
   * (twitching 2-3px between a waypoint and a physics blocker) stays within the radius
   * and is correctly detected as stuck, while genuine wall-slides that cover real
   * distance reset the timer. Used by both ChaseSystem and ReturnToPostBehavior.
   */
  requireMovementStall: boolean;

  /**
   * Total number of escalation stages before terminal action. When the escalation
   * level reaches this value, the detector fires the terminal stage and resets.
   * Standard configuration uses 4 (budget → reverse → nudge → terminal).
   */
  maxEscalation: number;
}

export class StuckDetector {
  private readonly stuckThresholdMs: number;
  private readonly opts: StuckDetectorOptions;

  private stuckTimer = 0;
  private _escalationLevel = 0;
  private bestDist = Infinity;

  /** Anchor position — set when the stuck timer begins accumulating. The entity
   *  must move at least STUCK_ANCHOR_RADIUS px from this point to clear the timer. */
  private anchorX = NaN;
  private anchorY = NaN;

  constructor(stuckThresholdMs: number, opts: StuckDetectorOptions) {
    this.stuckThresholdMs = stuckThresholdMs;
    this.opts = opts;
  }

  /**
   * Advance the detector by one tick.
   *
   * @param delta       Frame delta in milliseconds.
   * @param entityX     Entity world-space X this frame.
   * @param entityY     Entity world-space Y this frame.
   * @param distToTarget Current distance to the pathfinding target (px).
   * @param suppress    If true, skip accumulation this frame (e.g. a state transition
   *                    already resolved on this tick — ChaseSystem uses this to avoid
   *                    double-firing when a newState was set).
   */
  update(
    delta: number,
    entityX: number,
    entityY: number,
    distToTarget: number,
    suppress = false,
  ): StuckResult {
    // Check for distance-to-target progress.
    if (distToTarget < this.bestDist - this.opts.progressThresholdPx) {
      this.bestDist = distToTarget;
      this.stuckTimer = 0;
      this.anchorX = NaN;
      this.anchorY = NaN;
      if (!this.opts.persistEscalation) {
        this._escalationLevel = 0;
      }
      return { triggered: 0 };
    }

    if (suppress) {
      return { triggered: 0 };
    }

    // Accumulate stuck time — optionally gated on net displacement from anchor.
    if (this.opts.requireMovementStall) {
      // Initialize anchor on first accumulation frame.
      if (Number.isNaN(this.anchorX)) {
        this.anchorX = entityX;
        this.anchorY = entityY;
      }

      const adx = entityX - this.anchorX;
      const ady = entityY - this.anchorY;
      const distFromAnchor = Math.sqrt(adx * adx + ady * ady);

      if (distFromAnchor < STUCK_ANCHOR_RADIUS) {
        this.stuckTimer += delta;
      } else {
        // Entity escaped the anchor radius — genuinely moving.
        this.stuckTimer = 0;
        this.anchorX = entityX;
        this.anchorY = entityY;
      }
    } else {
      this.stuckTimer += delta;
    }

    if (this.stuckTimer < this.stuckThresholdMs) {
      return { triggered: 0 };
    }

    // Threshold crossed — advance escalation.
    this.stuckTimer = 0;
    this._escalationLevel++;

    if (this._escalationLevel >= this.opts.maxEscalation) {
      // Terminal stage — reset everything so a fresh cycle begins if the
      // entity gets stuck again at a different position.
      const level = this._escalationLevel;
      this._escalationLevel = 0;
      this.bestDist = Infinity;
      this.anchorX = NaN;
      this.anchorY = NaN;
      return { triggered: level };
    }

    // Non-terminal — anchor bestDist at the current distance so the next tick
    // doesn't see a false progress improvement (Infinity → actual distance)
    // and immediately clear escalation when persistEscalation=false.
    // Reset anchor so the next stage tracks from the current position.
    this.bestDist = distToTarget;
    this.anchorX = entityX;
    this.anchorY = entityY;
    return { triggered: this._escalationLevel };
  }

  /** Reset all stuck state (call on LOS reacquisition, arrival, target change, etc.). */
  reset(): void {
    this.stuckTimer = 0;
    this._escalationLevel = 0;
    this.bestDist = Infinity;
    this.anchorX = NaN;
    this.anchorY = NaN;
  }

  /** Current escalation level (0 = not escalated, 1+ = escalation stage).
   *  Callers use this for ongoing decisions — e.g. budget selection, reverse mode. */
  get escalationLevel(): number {
    return this._escalationLevel;
  }

  /** Reset only the distance-improvement tracking, preserving escalation state.
   *  Used when the pathfinding target changes mid-chase (new exit prediction leg). */
  resetProgress(): void {
    this.bestDist = Infinity;
    this.stuckTimer = 0;
    this.anchorX = NaN;
    this.anchorY = NaN;
  }
}
